/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useContext } from 'react';
import {
    object, arrayOf, shape, string, bool, number,
} from 'prop-types';
import { withStyles } from '@material-ui/core';
import { connect, useSelector } from 'react-redux';
import { getPassportBundleCollectionData } from '../../../../../../../../../state/ducks/Common/Common-Selectors';
import { getFeatureFlags } from '../../../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getActiveABTests } from '../../../../../../../../../state/ducks/App/ducks/ABTesting/ABTesting-Selectors';
import urlPromoHelper from '../../../../../../../../helpers/urlPromoHelper/urlPromoHelper';
import SkeletonStyles from '../../../../../../GraphqlSkeletonComponents/SkeletonStyles';

import UrlPromoBanner from './UrlPromoBanner';
import { getNewProductCardLayoutEnabled } from '../../../../../../../../../state/ducks/App/App-Selectors';

const VIEWPORT = {
    MOBILE: 'Mobile',
    DESKTOP: 'Desktop',
};

const styles = (theme) => ({
    ...SkeletonStyles,
    crossedOutRetailPrice: {
        textDecoration: 'line-through',
        fontWeight: 400,
    },
    crossedOutRetailPriceOneClick: {
        textDecoration: 'line-through',
        color: '#000',
        // fontWeight: 700,
    },
    salePrice: {
        color: `${theme.palette.pdp?.salePrice || '#a1001a'} !important`,
    },
    salePriceOneClick: {
        color: '#A1001A',
    },
    productName: {
        fontWeight: '400',
    },
    productFromCapitalize: {
        textTransform: 'capitalize',
    },
    wrappedPrice: {
        display: 'inline-block',
    },
    titleColor: {
        color: '#2F2F2F',
    },
    mobileCenteredPrice: {
        display: 'block',
        marginTop: '5px',
        color: theme.palette.product?.retailPrice || theme.palette.cms?.nonSaleItemPrice || theme.palette.cms?.primary || theme.palette.colorPrimary,
    },
    abTestPriceDesignFood: {
        marginBottom: '10px',
        fontSize: '16px',
        lineHeight: '20px',
        fontFamily: 'LatoBold',
        letterSpacing: '0.02em',
    },
    abTestPriceDesignFloral: {
        fontSize: '15px',
        lineHeight: '23px',
        fontFamily: 'LatoMedium',
    },
    abTestCrossedOut: {
        textDecoration: 'line-through',
        color: theme.palette.product?.salePrice || theme.palette.primaryButton,
    },
    abTestCrossedOutRetailPrice: {
        color: '#2F2F2F',
    },
    pullLeft: {
        display: 'block',
        textAlign: 'left',
    },
    priceMainContainer: {
        display: 'block',
        textAlign: 'left',
        '& $startingText': {
            lineHeight: '18px !important',
            fontSize: '14px !important',
            fontWeight: 500,
            '@media only screen and (max-width: 768px)': {
                lineHeight: '16px !important',
                fontSize: '12px !important',
                minHeight: 'auto',
            },
            '& $personalizeStartingText': {
                display: 'block !important',
                lineHeight: '1.063rem',
                fontSize: '0.75rem !important',
                fontFamily: theme.typography.fontFamily,
                fontWeight: '400 !important',
                letterSpacing: '0.01em',
                textAlign: 'left',
                textUnderlinePosition: 'from-font',
                textDecorationSkipInk: 'none',
                textTransform: 'lowercase',
            },
        },
        '& $productPriceContainer': {
            lineHeight: '30px !important',
            fontSize: '22px !important',
            '@media only screen and (max-width: 768px)': {
                lineHeight: '24px !important',
                fontSize: '18px !important',
            },
            '& s': {
                fontWeight: 'normal',
                marginRight: '2px',
            },
            '& $salePrice': {
                lineHeight: '30px !important',
                fontSize: '22px !important',
                '@media only screen and (max-width: 768px)': {
                    lineHeight: '24px !important',
                    fontSize: '18px !important',
                },
            },
        },
    },
    startingText: {
        display: 'block',
    },
    productPriceContainer: {
        display: 'flex',
    },
    upToProductPrice: {
        display: 'block',
        lineHeight: '21px !important',
        fontSize: '16px !important',
        '@media only screen and (max-width: 768px)': {
            lineHeight: '19px !important',
            fontSize: '14px !important',
        },
        '& s': {
            fontWeight: 'normal',
            marginRight: '2px',
            '@media only screen and (max-width: 768px)': {
                whiteSpace: 'nowrap',
                display: 'inline-block',
            },
        },
        '& $salePrice': {
            lineHeight: '21px !important',
            fontSize: '16px !important',
            '@media only screen and (max-width: 768px)': {
                lineHeight: '19px !important',
                fontSize: '14px !important',
                whiteSpace: 'nowrap',
                display: 'inline-block',
            },
        },
    },
    personalizeStartingText: {
        display: 'block !important',
        font: 'var(--comp-base-text-text4)',
        fontWeight: '500 !important',
        letterSpacing: '0.01em',
        textAlign: 'left',
        textUnderlinePosition: 'from-font',
        textDecorationSkipInk: 'none',
        textTransform: 'lowercase',
    },
    personalizeProductPriceContainer: {
        font: 'var(--comp-base-text-desktop-header5)',
        fontWeight: 700,
        letterSpacing: '0.01em',
        textAlign: 'left',
        '& s': {
            font: 'var(--comp-base-text-desktop-header5)',
            fontWeight: '500 !important',
            letterSpacing: '0.01em !important',
            textAlign: 'left',
        },
        '& span > strong:first-of-type': {
            font: 'var(--comp-base-text-desktop-header5)',
            fontWeight: '700 !important',
            letterSpacing: '0.01em !important',
            // paddingLeft: '0.313rem',
        },
    },
    hideStartAt: {
        visibility: 'hidden',
    },
});

const getPrices = (skuPriceRange, type, passportBundleCollectionData, forceItemPrice = false) => {
    if (!skuPriceRange) return {};
    const priceData = skuPriceRange[type];
    const startingDisplayPrice = priceData?.[0]?.display ? Number(priceData?.[0]?.display).toFixed(2) : '';
    const endingDisplayPrice = priceData?.[priceData.length - 1]?.display ? Number(priceData?.[priceData.length - 1]?.display).toFixed(2) : '';
    const startingDisplayPriceForVariantCA = priceData?.[0]?.value ? Number(priceData?.[0]?.value).toFixed(2) : '';
    const endingDisplayPriceForVariantCA = priceData?.[priceData.length - 1]?.value ? Number(priceData?.[priceData.length - 1]?.value).toFixed(2) : '';
    const showStartingDisplayPrice = startingDisplayPrice;
    const showEndingDisplayPrice = endingDisplayPrice;
    if (forceItemPrice) {
        if (passportBundleCollectionData?.enabled && passportBundleCollectionData?.price) {
            return {
                showStartingDisplayPrice,
                showEndingDisplayPrice,
                startingPrice: Number(priceData?.[0]?.value + passportBundleCollectionData.price).toFixed(2),
                endingPrice: Number(priceData?.[priceData.length - 1]?.value + passportBundleCollectionData.price).toFixed(2),
            };
        }
        return {
            startingDisplayPrice,
            endingDisplayPrice,
            startingPrice: Number(priceData?.[0]?.value).toFixed(2),
            endingPrice: Number(priceData?.[priceData.length - 1]?.value).toFixed(2),
        };
    }
    if (passportBundleCollectionData?.enabled && passportBundleCollectionData?.price) {
        return {
            startingDisplayPrice,
            endingDisplayPrice,
            startingPrice: Number((priceData?.[0]?.total || priceData?.[0]?.value) + passportBundleCollectionData.price).toFixed(2),
            endingPrice: (Number(priceData?.[priceData.length - 1]?.total || priceData?.[priceData.length - 1]?.value) + passportBundleCollectionData.price).toFixed(2),
        };
    }
    return {
        showStartingDisplayPrice,
        showEndingDisplayPrice,
        startingPrice: Number(priceData?.[0]?.total || priceData?.[0]?.value).toFixed(2),
        endingPrice: Number(priceData?.[priceData.length - 1]?.total || priceData?.[priceData.length - 1]?.value).toFixed(2),
    };
};

const PriceRange = ({
    priceRangeLayout,
    skuPriceRange,
    isMobile,
    classes,
    presentationFamily,
    passportBundleCollectionData,
    activeABTests,
    location,
    featureFlags,
    filterOptions,
    displayPrice,
    showSimpleProductRedesignAbTest,
    isCombiningPrices,
    priceForCombination,
    colorMatchTitle,
    noFromText,
    collectionRestructureEnabled,
    forceItemPriceOnly,
    showSinglePrice,
    collectionPageHierarchyEnabled,
    priceBreakDownItem,
    enablePersonalizedHPProductStyle,
    isAddOnWrapUpEnabled,
}) => {
    const viewport = isMobile ? VIEWPORT.MOBILE : VIEWPORT.DESKTOP;
    const isSSR = typeof window === 'undefined';
    const priceLayout = (priceRangeLayout || []).find((layout) => layout.viewport === viewport) || {};
    let { price_layout = '' } = priceLayout;
    const isFoodBrand = presentationFamily === 'food';
    const getProductCardLayout  = useSelector(getNewProductCardLayoutEnabled);
    const newProductCardEnabled = getProductCardLayout?.campaign?.campaignResponses?.[0]?.payload?.userGroup?.toLowerCase() === 'test';
    const showUpText = !noFromText ? '& Up' : '';
    // Default Layout type
    // All desktop views and food brands are using this format, if not defined in contentstack this will be used
    if (newProductCardEnabled) {
        price_layout = 'upTo $xxx.xx';
    } else if (price_layout === '' && !collectionPageHierarchyEnabled) {
        if (!collectionRestructureEnabled && (isFoodBrand || !isMobile)) {
            price_layout = '$xxx.xx - $xxx.xx';
        }
        if (activeABTests?.category_price_display === 'from' || collectionRestructureEnabled) {
            // allows for optimize test logic override
            price_layout = 'from $xxx.xx';
        }
        if (activeABTests?.category_price_display === 'lowest') {
            price_layout = '$xxx.xx';
        }
    } else if (collectionPageHierarchyEnabled) {
        price_layout = 'starting $xxx.xx';
    }
    // else use default case in switch

    let { startingPrice: saleStarting = '', endingPrice: saleEnding = '' } = (getPrices(skuPriceRange, 'sale', passportBundleCollectionData, forceItemPriceOnly));
    const { startingDisplayPrice: saleStartingDisplayPrice = '' } = (getPrices(skuPriceRange, 'sale', passportBundleCollectionData, forceItemPriceOnly));

    let { startingPrice: retailStarting = '', endingPrice: retailEnding = '' } = (getPrices(skuPriceRange, 'retail', passportBundleCollectionData, forceItemPriceOnly));
    const { endingDisplayPrice: retailEndingDisplayPrice = '', startingDisplayPrice: retailStartingDisplayPrice = '' } = (getPrices(skuPriceRange, 'retail', passportBundleCollectionData, forceItemPriceOnly));

    const disableOnAll = featureFlags?.['is-retail-price-overwrite']?.all || false;
    const disableOnPriceBreak = featureFlags?.['is-retail-price-overwrite']?.priceBreakdown || false;

    let overWritePrice = true; // by default
    if (disableOnAll) {
        overWritePrice = false;
    } else if (disableOnPriceBreak) {
        if (priceBreakDownItem) {
            overWritePrice = false;
        }
    }

    if (overWritePrice) {
        if (retailStartingDisplayPrice && retailStarting !== retailStartingDisplayPrice) retailStarting = retailStartingDisplayPrice;
        if (retailEndingDisplayPrice && retailEnding !== retailEndingDisplayPrice) retailEnding = retailEndingDisplayPrice;
    }
    const isPassportBundleCollection = passportBundleCollectionData?.enabled && passportBundleCollectionData?.price;
    let isSale = Number(saleStarting) < Number(retailStarting) || Number(saleEnding) < Number(retailEnding);

    if (!isSale && saleStartingDisplayPrice && saleStartingDisplayPrice !== 'NaN' && retailEndingDisplayPrice && retailEndingDisplayPrice !== 'NaN' && !noFromText) {
        isSale = Number(saleStarting) !== Number(saleStartingDisplayPrice) || Number(retailEnding) !== Number(retailEndingDisplayPrice);
    }
    let displayPromoBanner = false;

    let salePriceText =  '';
    let retailPriceText = '';

    const windowLocation = location?.search ?? '';
    const urlPromoPackage = urlPromoHelper.parser(windowLocation);

    /**
     * URL promo logic should be DISABLED when:
     * - The product is already on sale.
     * - the product is apart of Passport Bundle.
     */
    if (!isSale && !isPassportBundleCollection && featureFlags['is-url-promo-enabled'] && urlPromoPackage.hasPromo) {
        /**
         * The current URL promo offerings have 2 scenarios:
         *  1. There is a monetary discount which should display the banner and the discount as the sale price.
         *  2. Free shipping which doesnt affect the price at all but should show banner.
         */

        // show banner
        displayPromoBanner = true;

        // if its not free shipping, calculate the new prices with discounts
        if (urlPromoPackage.type !== 'f') {
            // enable sale display for promo pricing
            isSale = true;
            // calc the new sale price using retail prices
            saleStarting = urlPromoHelper.calcPrice(urlPromoPackage, retailStarting);
            saleEnding = urlPromoHelper.calcPrice(urlPromoPackage, retailEnding);
        }
    }

    const filterOptionsDisplay = (filters = {}, start, end) => {
        const startPrice = start && parseFloat(start).toFixed(2);
        const endPrice = end && parseFloat(end).toFixed(2);
        if (startPrice || endPrice) {
            if (filters?.highToLowBtn) {
            // show ending/highest price
                return `$${endPrice}`;
            }
            // show starting/lowest price
            return `$${startPrice}`;
        }
        return '';
    };

    if (displayPrice === false || isSSR) {
        return (
            <div className={`${classes.textLG} ${classes.shimmer}`} data-testid="sale-price">
                {' '}
                {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />}
            </div>
        );
    }

    if (isCombiningPrices) {
        const {
            startingPrice: saleStarting2 = '',
            // endingPrice: saleEnding2 = '',
        } = getPrices(priceForCombination, 'sale', passportBundleCollectionData, forceItemPriceOnly);
        const {
            startingPrice: retailStarting2 = '',
            // endingPrice: retailEnding2 = '',
        } = getPrices(priceForCombination, 'retail', passportBundleCollectionData, forceItemPriceOnly);

        const comboRetail2 = (parseFloat(retailStarting2) + parseFloat(retailStarting)).toFixed(2);
        const comboSale2 = (parseFloat(saleStarting2) + parseFloat(saleStarting)).toFixed(2);

        return (
            <span data-testid="combined-price">
                <span className={classes.productName} />
                <span>
                    {isSale ? `$${comboSale2}` : `$${comboRetail2}`}
                </span>
                {/* {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />} */}
            </span>
        );
    }
    const hideStartingAt = isAddOnWrapUpEnabled ? retailStarting === retailEnding : '';

    switch (price_layout) {
        // Used by food & flowers desktop
        case '$xxx.xx - $xxx.xx':
            retailPriceText = retailStarting !== retailEnding ? `$${retailStarting} - $${retailEnding}` : filterOptionsDisplay(filterOptions, retailStarting, retailEnding);
            salePriceText = saleStarting !== saleEnding ? `$${saleStarting} - $${saleEnding}` : filterOptionsDisplay(filterOptions, saleStarting, saleEnding);
            // ABTEST
            if (activeABTests?.mobile_simple_product_redesign && showSimpleProductRedesignAbTest) {
                if (isSale && retailPriceText !== salePriceText) {
                    return (
                        showSinglePrice ? (
                            <span className={classes.productName}>
                                <span>
                                    {`${priceLayout.sale_text ? `${priceLayout.sale_text} ` : ''}${salePriceText}`}
                                </span>
                                {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />}
                            </span>
                        ) : (
                            <span data-testid="sale-price" className={`${isMobile && classes.abTestPriceDesignFood}`}>
                                <span className={classes.abTestCrossedOut}>
                                    <s className={`${classes.abTestCrossedOutRetailPrice} crossed-price`}>
                                        {retailPriceText}
                                    </s>
                                </span>
                                {' '}
                                <span className={`${classes.salePrice} ${classes.wrappedPrice} sale-price`}>
                                    {`${priceLayout.sale_text ? `${priceLayout.sale_text} ` : ''}${salePriceText}`}
                                </span>
                            </span>
                        )

                    );
                }
                return (
                    <span data-testid="retail-price-range" className={`${isMobile && classes.abTestPriceDesignFood}`}>
                        {retailPriceText}
                    </span>
                );
            }

            if (isSale && retailPriceText !== salePriceText) {
                return (
                    showSinglePrice ? (
                        <span className={classes.productName}>
                            <span>
                                {`${priceLayout.sale_text ? `${priceLayout.sale_text} ` : ''}${salePriceText}`}
                            </span>
                            {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />}
                        </span>
                    ) : (
                        <span className={isMobile ? classes.mobileCenteredPrice : ''} data-testid="sale-price">
                            <s className={`${classes.wrappedPrice} crossed-price`}>
                                {retailPriceText}
                            </s>
                            {' '}
                            <span className={`${classes.salePrice} ${classes.wrappedPrice} sale-price`}>
                                {`${priceLayout.sale_text ? `${priceLayout.sale_text} ` : ''}${salePriceText}`}
                            </span>
                            {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />}
                        </span>
                    )
                );
            }
            return (
                <span data-testid="retail-price-range">
                    <span className={`${colorMatchTitle ? classes.titleColor : ''}  ${isMobile ? classes.mobileCenteredPrice : ''}`}>
                        {retailPriceText}
                    </span>
                    {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />}
                </span>
            );

        // Used by 18F
        default:
        case 'from $xxx.xx':
            retailPriceText = filterOptionsDisplay(filterOptions, retailStarting, retailEnding);
            salePriceText = filterOptionsDisplay(filterOptions, saleStarting, saleEnding);
            if (activeABTests?.mobile_simple_product_redesign && showSimpleProductRedesignAbTest) {
                if (isSale && retailPriceText !== salePriceText) {
                    return (
                        showSinglePrice ? (
                            <span className={classes.productName}>
                                <span>
                                    {`${priceLayout.sale_text ? `${priceLayout.sale_text} ` : ''}${salePriceText}`}
                                </span>
                                {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />}
                            </span>
                        ) : (
                            <span data-testid="sale-price" className={classes.abTestPriceDesignFloral}>
                                <span>
                                    {'from '}
                                    <strong>
                                        <span className={classes.abTestCrossedOut}>
                                            <span className={`${classes.abTestCrossedOutRetailPrice} crossed-price`}>
                                                {retailPriceText}
                                            </span>
                                        </span>
                                        {/* <span className={classes.abTestCrossedOutPrice}>
                                        {retailPriceText}
                                    </span> */}
                                    </strong>
                                </span>
                                {' '}
                                <span className={`${classes.salePrice}`}>
                                    <strong>
                                        {`${priceLayout.sale_text ? `${priceLayout.sale_text} ` : ''}${salePriceText}`}
                                    </strong>
                                </span>
                                {/* {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />} */}
                            </span>
                        )
                    );
                }
                return (
                    <span data-testid="retail-price-range" className={classes.abTestPriceDesignFloral}>
                        <span>
                            {'from '}
                        </span>
                        <span>
                            <strong>
                                {retailPriceText}
                            </strong>
                        </span>
                        {/* {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />} */}
                    </span>
                );
            }
            if (isSale && retailPriceText !== salePriceText) {
                return (
                    showSinglePrice ? (
                        <span className={classes.productName}>
                            <span>
                                {`${priceLayout.sale_text ? `${priceLayout.sale_text} ` : ''}${salePriceText}`}
                            </span>
                            {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />}
                        </span>
                    ) : (
                        <span className={noFromText ? classes.pullLeft : ''} data-testid="sale-price">
                            <span className={classes.productName}>
                                <strong className={classes.productFromCapitalize}>
                                    {!noFromText && 'from '}
                                </strong>
                                <s>
                                    <strong>
                                        {retailPriceText?.toString?.() !== salePriceText?.toString?.() ? retailPriceText : retailStartingDisplayPrice}
                                    </strong>
                                </s>

                            </span>
                            {' '}
                            <span className={`${classes.salePrice}`}>
                                {`${priceLayout.sale_text ? `${priceLayout.sale_text} ` : ''}${salePriceText}`}
                            </span>
                            {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />}
                        </span>
                    )
                );
            }
            return (
                <span className={noFromText ? classes.pullLeft : ''} data-testid="retail-price-range">
                    {!noFromText && (
                        <span className={classes.productName}>
                            <strong className={classes.productFromCapitalize}>
                                {'from '}
                            </strong>
                        </span>
                    )}
                    <span>
                        {retailPriceText}
                    </span>
                    {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />}
                </span>
            );
        case 'starting $xxx.xx':
            retailPriceText = filterOptionsDisplay(filterOptions, retailStarting, retailEnding);
            salePriceText = filterOptionsDisplay(filterOptions, saleStarting, saleEnding);
            if (activeABTests?.mobile_simple_product_redesign && showSimpleProductRedesignAbTest) {
                if (isSale && retailPriceText !== salePriceText) {
                    return (
                        showSinglePrice ? (
                            <span className={classes.productName}>
                                <span>
                                    {`${priceLayout.sale_text ? `${priceLayout.sale_text} ` : ''}${salePriceText}`}
                                </span>
                                {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />}
                            </span>
                        ) : (
                            <span data-testid="sale-price" className={classes.abTestPriceDesignFloral}>
                                <span className={`${enablePersonalizedHPProductStyle ? classes.personalizeStartingText : classes.startingText }`}>
                                    {'starting at '}
                                </span>
                                {' '}
                                <span className={`${enablePersonalizedHPProductStyle ? classes.personalizeProductPriceContainer : classes.productPriceContainer}`}>
                                    <span className={classes.abTestCrossedOut}>
                                        <span className={`${classes.abTestCrossedOutRetailPrice} crossed-price`}>
                                            {retailPriceText}
                                        </span>
                                    </span>
                                    <span className={`${classes.salePrice}`}>
                                        <strong>
                                            {`${priceLayout.sale_text ? `${priceLayout.sale_text} ` : ''}${salePriceText}`}
                                        </strong>
                                    </span>
                                </span>
                            </span>
                        )
                    );
                }
                return (
                    <span data-testid="retail-price-range" className={classes.abTestPriceDesignFloral}>
                        <span className={`${enablePersonalizedHPProductStyle ? classes.personalizeStartingText : classes.startingText }`}>
                            {'starting at '}
                        </span>
                        <span className={`${enablePersonalizedHPProductStyle ? classes.personalizeProductPriceContainer : classes.productPriceContainer}`}>
                            <strong>
                                {retailPriceText}
                            </strong>
                        </span>
                    </span>
                );
            }
            if (isSale && retailPriceText !== salePriceText) {
                return (
                    <span className={classes.priceMainContainer} data-testid="sale-price">
                        <span className={`${enablePersonalizedHPProductStyle ? classes.personalizeStartingText : classes.startingText} ${hideStartingAt ? classes.hideStartAt : ''}`}>
                            {!noFromText && 'starting at '}
                        </span>
                        <span className={`${enablePersonalizedHPProductStyle ? classes.personalizeProductPriceContainer : classes.productPriceContainer}`}>
                            <s>
                                {retailPriceText?.toString?.() !== salePriceText?.toString?.() ? retailPriceText : retailStartingDisplayPrice}
                            </s>
                            <span className={`${classes.salePrice}`}>
                                <strong>
                                    {` ${priceLayout.sale_text ? `${priceLayout.sale_text} ` : ''}${salePriceText}`}
                                </strong>
                            </span>
                        </span>
                        {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />}
                    </span>
                );
            }
            return (
                <span className={classes.priceMainContainer} data-testid="retail-price-range">
                    {!noFromText && (
                        <span className={`${enablePersonalizedHPProductStyle ? classes.personalizeStartingText : classes.startingText} ${hideStartingAt ? classes.hideStartAt : ''}`}>
                            {'starting at '}
                        </span>
                    )}
                    <span className={`${enablePersonalizedHPProductStyle ? classes.personalizeProductPriceContainer : classes.productPriceContainer}`}>
                        {retailPriceText}
                    </span>
                    {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />}
                </span>
            );
        case 'upTo $xxx.xx':
            retailPriceText = filterOptionsDisplay(filterOptions, retailStarting, retailEnding);
            salePriceText = filterOptionsDisplay(filterOptions, saleStarting, saleEnding);
            if (activeABTests?.mobile_simple_product_redesign && showSimpleProductRedesignAbTest) {
                if (isSale && retailPriceText !== salePriceText) {
                    return (
                        showSinglePrice ? (
                            <span className={classes.productName}>
                                <span>
                                    {`${priceLayout.sale_text ? `${priceLayout.sale_text} ` : ''}${salePriceText} ${showUpText}`}
                                </span>
                                {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />}
                            </span>
                        ) : (
                            <span data-testid="sale-price" className={classes.abTestPriceDesignFloral}>
                                <span className={`${classes.upToProductPrice}`}>
                                    <span className={classes.abTestCrossedOut}>
                                        <span className={`${classes.abTestCrossedOutRetailPrice} crossed-price`}>
                                            { `${retailPriceText}`}
                                        </span>
                                    </span>
                                    <span className={`${classes.salePrice}`}>
                                        <strong>
                                            {`${priceLayout.sale_text ? `${priceLayout.sale_text}` : ''}${salePriceText} ${showUpText}`}
                                        </strong>
                                    </span>
                                </span>
                            </span>
                        )
                    );
                }
                return (
                    <span data-testid="retail-price-range" className={classes.abTestPriceDesignFloral}>
                        <span className={`${classes.upToProductPrice}`}>
                            <strong>
                                {`${retailPriceText} ${showUpText}`}
                            </strong>
                        </span>
                    </span>
                );
            }
            if (isSale && retailPriceText !== salePriceText) {
                return (
                    <span className={classes.priceMainContainer} data-testid="sale-price">
                        <span className={`${ classes.upToProductPrice}`}>
                            <s>
                                {`${retailPriceText?.toString?.() !== salePriceText?.toString?.() ? retailPriceText : retailStartingDisplayPrice}`}
                            </s>
                            <span className={`${classes.salePrice}`}>
                                <strong>
                                    {` ${priceLayout.sale_text ? `${priceLayout.sale_text} ` : ''}${salePriceText} ${showUpText}`}
                                </strong>
                            </span>
                        </span>
                        {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />}
                    </span>
                );
            }
            return (
                <span className={classes.priceMainContainer} data-testid="retail-price-range">
                    <span className={`${classes.upToProductPrice}`}>
                        {`${retailPriceText} ${showUpText}`}
                    </span>
                    {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />}
                </span>
            );
        case '$xxx.xx':
            retailPriceText = filterOptionsDisplay(filterOptions, retailStarting, retailEnding);
            salePriceText = filterOptionsDisplay(filterOptions, saleStarting, saleEnding);
            if (isSale && retailPriceText !== salePriceText) {
                return (
                    <span data-testid="sale-price">
                        <span className={classes.productName}>
                            <s>
                                {retailPriceText}
                            </s>
                        </span>
                        {' '}
                        <span className={`${classes.salePrice} sale-price`}>
                            {`${priceLayout.sale_text ? `${priceLayout.sale_text} ` : ''}${salePriceText}`}
                        </span>
                        {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />}
                    </span>
                );
            }
            return (
                <span data-testid="retail-price-range">
                    <span className={classes.productName} />
                    <span>
                        {retailPriceText}
                    </span>
                    {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />}
                </span>
            );
        case 'one-click':
            retailPriceText = filterOptionsDisplay(filterOptions, retailStarting, retailEnding);
            salePriceText = filterOptionsDisplay(filterOptions, saleStarting, saleEnding);
            if (isSale && retailPriceText !== salePriceText) {
                return (
                    <span data-testid="sale-price-oc">
                        <span className={`${classes.salePriceOneClick} sale-price`}>
                            {`${priceLayout.sale_text ? `${priceLayout.sale_text} ` : ''}${salePriceText}`}
                        </span>
                        {' '}
                        <span className={classes.productName}>
                            <span id="crossedOutPrice" className={`${classes.crossedOutRetailPriceOneClick} crossed-price`}>
                                {retailPriceText}
                            </span>
                        </span>
                        {/* {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />} */}
                    </span>
                );
            }
            return (
                <span id="rprrpr" data-testid="retail-price-range">
                    <span className={classes.productName} />
                    <span>
                        {retailPriceText}
                    </span>
                    {displayPromoBanner && <UrlPromoBanner urlPromoPackage={urlPromoPackage} />}
                </span>
            );
    }
};

PriceRange.propTypes = {
    featureFlags: object.isRequired,
    presentationFamily: string.isRequired,
    priceRangeLayout: arrayOf(shape({
        price_layout: string,
        sale_text: string,
        viewport: string,
    })),
    skuPriceRange: shape({
        retail: arrayOf(shape({
            value: number,
        })),
        sale: arrayOf(shape({
            value: number,
        })),
    }),
    isMobile: bool,
    classes: object.isRequired,
    passportBundleCollectionData: shape({
        enabled: bool,
        price: number,
    }),
    activeABTests: object,
    location: shape({
        search: string,
    }),
    filterOptions: object,
    displayPrice: bool.isRequired,
    showSimpleProductRedesignAbTest: bool.isRequired,
    isCombiningPrices: bool,
    priceForCombination: shape({
        retail: arrayOf(shape({
            value: number,
        })),
        sale: arrayOf(shape({
            value: number,
        })),
    }),
    colorMatchTitle: bool,
    noFromText: bool,
    collectionRestructureEnabled: bool,
    forceItemPriceOnly: bool,
    showSinglePrice: bool,
    collectionPageHierarchyEnabled: bool,
    priceBreakDownItem: bool,
    enablePersonalizedHPProductStyle: bool,
    isAddOnWrapUpEnabled: bool,
};

PriceRange.defaultProps = {
    priceRangeLayout: [
        {
            price_layout: '',
            sale_text: '',
            viewport: VIEWPORT.DESKTOP,
        },
        {
            price_layout: '',
            sale_text: '',
            viewport: VIEWPORT.MOBILE,
        },
    ],
    skuPriceRange: {},
    isMobile: false,
    passportBundleCollectionData: {},
    activeABTests: {},
    location: {
        search: '',
    },
    filterOptions: {},
    isCombiningPrices: false,
    priceForCombination: {},
    colorMatchTitle: false,
    noFromText: false,
    collectionRestructureEnabled: false,
    forceItemPriceOnly: false,
    showSinglePrice: false,
    collectionPageHierarchyEnabled: false,
    priceBreakDownItem: false,
    enablePersonalizedHPProductStyle: false,
    isAddOnWrapUpEnabled: false,
};

const mapStateToProps = (state) => ({
    featureFlags: getFeatureFlags(state),
    passportBundleCollectionData: getPassportBundleCollectionData(state),
    activeABTests: getActiveABTests(state),
});

export default connect(mapStateToProps)(withStyles(styles)(PriceRange));
